import { ArrowRight, ShieldCheck, Wand2 } from 'lucide-react';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BUILT_IN_PRESETS, type BuiltInPresetId, type Preset } from '@/core/project/preset';
import { formatBytes } from '@/core/time/format';
import { cn } from '@/lib/cn';
import { summarize } from '@/lib/network-ledger';
import { pickVideoFiles, VIDEO_ACCEPT } from '@/lib/video-files';
import { useLedgerStore } from '@/state/ledger-store';
import { usePresetStore } from '@/state/preset-store';
import { useFilePicker } from '../editor/media/use-file-picker';
import { Button, Spinner } from '../primitives';
import { PRESET_ICONS } from '../presets/preset-icons';

/**
 * Recipes for what people make from a video, each a band of its own. "Just a
 * video" (nothing applied) comes first: it is the one chosen when nothing else
 * is, so a video dropped without a choice opens in the plain editor. Then the
 * everyday jobs, the niche last. Recording a new video isn't editing one, so it
 * lives in the hero instead (`RecordCallout`); its recipe is applied when the
 * recording opens.
 */
const BANDS = ['blank', 'reels', 'youtube', 'motovlog'] as const satisfies readonly BuiltInPresetId[];
type BandId = (typeof BANDS)[number];

/** Recipes that do one job, chosen in one band below the others. */
const JOBS = ['faces', 'plates', 'fillers', 'captions'] as const satisfies readonly BuiltInPresetId[];

/** Recipes never shown as a choice here: recording starts from the hero, not from a file. */
const NOT_CHOICES: readonly BuiltInPresetId[] = ['recording'];

/** The jobs on show: a recipe only a content page's link opens (faces and plates together, say) joins them while it is the chosen one. */
function jobsWith(activeId: string): readonly BuiltInPresetId[] {
  const linked = BUILT_IN_PRESETS.find((preset) => preset.id === activeId)?.builtIn;
  const shown = [...BANDS, ...JOBS, ...NOT_CHOICES] as readonly string[];
  return linked && !shown.includes(linked) ? [...JOBS, linked] : JOBS;
}

/** Real output of the app's own pipeline on royalty-free photos (see scripts/home-thumbnails.gen.ts). */
const THUMBNAILS: Record<BandId, { readonly src: string; readonly portrait: boolean }> = {
  blank: { src: '/home/blank.webp', portrait: false },
  reels: { src: '/home/reels.webp', portrait: true },
  youtube: { src: '/home/youtube.webp', portrait: false },
  motovlog: { src: '/home/motovlog.webp', portrait: false },
};

const builtIn = (id: BuiltInPresetId): Preset => BUILT_IN_PRESETS.find((preset) => preset.builtIn === id)!;

const band = (id: string): CSSProperties => ({ viewTransitionName: `band-${id}` }) as CSSProperties;

/**
 * Every band shares one grid with fixed tracks — row header | title and promise |
 * detail or drop target | result — so column edges line up whichever band is open,
 * and the result track fits the widest (landscape) thumbnail.
 */
const BAND_GRID = 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-5 gap-y-4 lg:grid-cols-[130px_minmax(0,1fr)_340px_176px] lg:gap-x-8';

function Thumb({ id, size }: { readonly id: BandId; readonly size: 'small' | 'large' }) {
  const { t } = useTranslation();
  const { src, portrait } = THUMBNAILS[id];
  return (
    <img
      src={src}
      alt={t('home.thumbAlt', { title: t(`home.intents.${id}.title`) })}
      loading="lazy"
      decoding="async"
      className={cn(
        'shrink-0 rounded-xl object-cover ring-1 ring-line',
        size === 'large' ? 'h-18 sm:h-24' : 'h-16 lg:h-19',
        portrait ? 'aspect-9/16' : 'aspect-video',
      )}
    />
  );
}

/** The drop target inside the active band (or the convert panel); trust is shown right where the file goes. */
export function DropTarget({
  dragging,
  loading,
  onFiles,
  hint,
}: {
  readonly dragging: boolean;
  readonly loading: string | false;
  readonly onFiles: (files: File[]) => void;
  /** What happens to the files; the default speaks of joining several. */
  readonly hint?: string;
}) {
  const { t, i18n } = useTranslation();
  const picker = useFilePicker(VIDEO_ACCEPT, (files) => {
    const videos = pickVideoFiles(files);
    if (videos.length > 0) onFiles(videos);
  });
  const entries = useLedgerStore((state) => state.entries);
  const uploaded = useMemo(() => summarize(entries).uploadedBytes, [entries]);
  return (
    <div
      className={cn(
        'flex min-h-30 flex-col items-center justify-center gap-2.5 rounded-2xl border-[1.5px] border-dashed px-5 py-4 text-center transition-colors duration-200',
        dragging ? 'border-accent-strong bg-accent-soft' : 'border-accent-strong/70 bg-accent-soft/50',
      )}
    >
      {loading ? (
        <p className="flex items-center gap-2.5 text-sm font-medium text-fg-muted">
          <Spinner className="text-accent-text" />
          {loading}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <p className="text-[17px] font-semibold tracking-[-0.02em] text-fg">{dragging ? t('home.dropActive') : t('home.dropHere')}</p>
            <Button variant="primary" onClick={picker.pick}>
              {t('home.choose')}
            </Button>
          </div>
          <p className="text-[13px] leading-snug text-balance text-fg-muted">{hint ?? t('home.dropHint')}</p>
          <p className="flex items-center gap-1.5 text-[13px] leading-snug text-fg-muted tabular">
            <ShieldCheck size={14} className="shrink-0 text-accent-text" />
            {t('home.dropTrust', { size: formatBytes(uploaded, i18n.language) })}
          </p>
        </>
      )}
      {picker.input}
    </div>
  );
}

/** Title and promise; on phones the recipe's tag follows them instead of sitting above as a label. */
function BandCopy({ id }: { readonly id: BandId }) {
  const { t } = useTranslation();
  return (
    <span className="min-w-0">
      <span className="block text-[21px] leading-tight font-semibold tracking-[-0.03em] text-balance text-fg sm:text-[24px]">{t(`home.intents.${id}.title`)}</span>
      <span className="mt-1 block text-[15px] leading-snug text-pretty text-fg-muted">{t(`home.intents.${id}.promise`)}</span>
      <span className="mt-1 block text-[13px] text-fg-subtle lg:hidden">{t(`home.intents.${id}.tag`)}</span>
    </span>
  );
}

const RowHeader = ({ children }: { readonly children: string }) => <span className="hidden text-[13px] font-medium text-fg-muted lg:block">{children}</span>;

/** A choice inside a band (one job, or one of the person's own recipes): the chosen one is the live choice. */
function ChoiceChip({ chosen, onSelect, icon, children }: { readonly chosen: boolean; readonly onSelect: () => void; readonly icon: ReactNode; readonly children: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={chosen}
      onClick={onSelect}
      className={cn(
        'inline-flex h-10 items-center gap-2 rounded-xl px-3.5 text-sm font-medium ring-1 transition-colors duration-150 ring-inset outline-none focus-visible:ring-2 focus-visible:ring-accent',
        chosen ? 'bg-accent-soft text-fg ring-accent-strong/60 [&>svg]:text-accent-text' : 'bg-elevated text-fg-muted ring-line hover:text-fg',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** Bands that hold choices: a label, the choices, and once one of them is chosen, the band rises and takes the file. */
function ChoiceBand({
  id,
  label,
  raised,
  children,
  below,
}: {
  readonly id: string;
  readonly label: string;
  readonly raised: boolean;
  readonly children: ReactNode;
  /** What shows under the choices while one of them is chosen. */
  readonly below: ReactNode;
}) {
  return (
    <div style={band(id)} className={cn(raised ? RAISED : 'border-b border-line-strong px-2 py-4')}>
      <div className="grid gap-x-8 gap-y-3 lg:grid-cols-[130px_minmax(0,1fr)]">
        <span className="text-[13px] font-medium text-fg-muted lg:pt-2.5">{label}</span>
        <div className="flex flex-wrap gap-2">{children}</div>
      </div>
      {raised && below}
    </div>
  );
}

const RAISED = 'relative z-10 -mx-3 my-[-1px] rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line sm:-mx-5 sm:p-5';

export interface IntentBandsProps {
  readonly activeId: string;
  readonly onSelect: (preset: Preset) => void;
  readonly onFiles: (files: File[]) => void;
  readonly dragging: boolean;
  /** What is happening while files are being prepared, or false. */
  readonly loading: string | false;
}

/**
 * "What are you making?" as full-width ruled bands. Each band is a recipe; the
 * chosen one rises into a panel and holds the drop target.
 */
export function IntentBands({ activeId, onSelect, onFiles, dragging, loading }: IntentBandsProps) {
  const { t } = useTranslation();
  const mine = usePresetStore((state) => state.presets);
  const activeMine = mine.find((preset) => preset.id === activeId);
  const jobs = jobsWith(activeId);
  const activeJob = jobs.map(builtIn).find((preset) => preset.id === activeId);

  return (
    <div role="radiogroup" aria-label={t('home.intentsLabel')} className="border-t border-line-strong">
      {BANDS.map((id) => {
        const preset = builtIn(id);
        if (preset.id === activeId) {
          return (
            <div key={preset.id} role="radio" aria-checked tabIndex={0} style={band(preset.id)} className={cn(BAND_GRID, RAISED)}>
              <RowHeader>{t(`home.intents.${id}.tag`)}</RowHeader>
              <span className="min-w-0">
                <BandCopy id={id} />
                <span className="mt-2 hidden text-[13px] leading-snug text-pretty text-fg-subtle lg:block">{t(`home.intents.${id}.detail`)}</span>
              </span>
              <div className="col-span-2 row-start-2 lg:col-span-1 lg:col-start-3 lg:row-start-1">
                <DropTarget dragging={dragging} loading={loading} onFiles={onFiles} />
              </div>
              <span className="lg:col-start-4 lg:justify-self-end">
                <Thumb id={id} size="large" />
              </span>
            </div>
          );
        }
        return (
          <button
            key={preset.id}
            type="button"
            role="radio"
            aria-checked={false}
            style={band(preset.id)}
            onClick={() => onSelect(preset)}
            className={cn(BAND_GRID, 'group w-full border-b border-line-strong px-2 py-4 text-left transition-colors duration-200 hover:bg-panel/70')}
          >
            <RowHeader>{t(`home.intents.${id}.tag`)}</RowHeader>
            <BandCopy id={id} />
            <span className="hidden items-center justify-between gap-4 lg:flex">
              <span className="text-[13px] leading-snug text-pretty text-fg-subtle">{t(`home.intents.${id}.detail`)}</span>
              <ArrowRight size={18} className="shrink-0 -translate-x-1 text-fg-subtle opacity-0 transition-all duration-200 ease-out-soft group-hover:translate-x-0 group-hover:opacity-100" />
            </span>
            <span className="justify-self-end">
              <Thumb id={id} size="small" />
            </span>
          </button>
        );
      })}

      <ChoiceBand
        id="jobs"
        label={t('home.jobsLabel')}
        raised={activeJob !== undefined}
        below={
          activeJob?.builtIn && (
            // The same columns as the bands above, so the drop target lines up with theirs.
            <div className={cn(BAND_GRID, 'mt-5 border-t border-line pt-5')}>
              <span className="hidden lg:block" />
              <span className="col-span-2 min-w-0 lg:col-span-1">
                <span className="block text-[17px] leading-snug font-medium tracking-[-0.01em] text-pretty text-fg">{t(`home.intents.${activeJob.builtIn}.promise`)}</span>
                <span className="mt-1.5 block text-[13px] leading-snug text-pretty text-fg-subtle">{t(`home.intents.${activeJob.builtIn}.detail`)}</span>
              </span>
              <div className="col-span-2 lg:col-span-1 lg:col-start-3">
                <DropTarget dragging={dragging} loading={loading} onFiles={onFiles} />
              </div>
            </div>
          )
        }
      >
        {jobs.map((id) => {
          const preset = builtIn(id);
          const Icon = PRESET_ICONS[id];
          return (
            <ChoiceChip key={id} chosen={preset.id === activeId} onSelect={() => onSelect(preset)} icon={<Icon size={15} />}>
              {t(`home.intents.${id}.title`)}
            </ChoiceChip>
          );
        })}
      </ChoiceBand>

      {mine.length > 0 && (
        <ChoiceBand
          id="mine"
          label={t('home.mine')}
          raised={activeMine !== undefined}
          below={
            <div className="mt-4 lg:ml-40.5">
              <DropTarget dragging={dragging} loading={loading} onFiles={onFiles} />
            </div>
          }
        >
          {mine.map((preset) => (
            <ChoiceChip key={preset.id} chosen={preset.id === activeId} onSelect={() => onSelect(preset)} icon={<Wand2 size={15} />}>
              {preset.name ?? ''}
            </ChoiceChip>
          ))}
        </ChoiceBand>
      )}
    </div>
  );
}
